// DestructiveActionConfirm
//
// Phase B3 (May 2026 — data hygiene gap recovery): unified
// confirmation dialog for destructive actions across the app
// (archive, merge, unmerge, soft-delete, dismiss, etc.).
//
// Per the user's original ask:
//   "Show exactly what each action does in plain language before
//    commit. Good duplicate systems make outcomes obvious at the
//    moment of decision."
//
// Today the destructive-confirm experience is a mix of `window.confirm`
// (which can't render structure or per-action consequences) and ad-hoc
// modals. This component standardizes the shape:
//
//   * Title (action name)
//   * Optional warning banner (e.g., "Not reversible from the UI")
//   * Plain-English consequences as bullet points (what moves, what
//     stays, what changes, who sees it)
//   * Optional reason field (audit-logged) — required for most
//     destructive actions, optional for some
//   * Cancel / Confirm buttons
//
// The component does NOT execute the action — the caller passes
// `onConfirm` and is responsible for the actual API call. The reason
// text is surfaced via the resolved `confirm` callback's argument.

import { useEffect, useState, type ReactNode } from "react";

import { Button, ErrorBanner, Stack } from "../ui";
import { Modal } from "./Modal";
import { TypeToConfirmGate } from "./TypeToConfirmGate";

export interface DestructiveActionConfirmProps {
  /** Modal open flag — when false, nothing renders. */
  open: boolean;

  /** Bold action name shown in the title (e.g., "Archive contact"). */
  actionName: string;

  /**
   * Plain-English consequences. Each entry renders as a bullet point.
   * Phrase as side-effects ("Activities, tags, and notes follow the
   * contact"), NOT as questions or instructions. Empty list renders no
   * list at all (use this for trivial confirms).
   */
  consequences: ReadonlyArray<string>;

  /** Optional reversibility note rendered as a footer hint. */
  reversibilityNote?: string;

  /** Optional extra content (e.g. a disposition choice) rendered
   *  between the consequences list and the reason field. */
  children?: ReactNode;

  /** Whether the reason field is shown. Defaults to true. */
  showReasonField?: boolean;

  /**
   * When true, the reason input is required — Confirm is disabled
   * until the user types something. Defaults to false (reason
   * captured for audit but optional).
   */
  reasonRequired?: boolean;

  /** Pre-filled reason text. */
  initialReason?: string;

  /** Placeholder for the reason textarea. */
  reasonPlaceholder?: string;

  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;

  /** "danger" → red Confirm button; "primary" → blue. Defaults "danger". */
  confirmTone?: "danger" | "primary";

  /** Optional warning banner above the consequences (red-tinted). */
  warningBanner?: string;

  /**
   * When set, embeds a type-to-confirm gate (TypeToConfirmGate): the user
   * must literally type this phrase before Confirm enables. Use for
   * restricted-tier / high-blast-radius actions (bulk merge, permanent
   * delete, clearing a security control). Omit for standard confirms.
   */
  confirmPhrase?: string;

  /** Explanation shown above the type-to-confirm input. */
  confirmPhraseExplanation?: ReactNode;

  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;

  /** External "running" state — disables both buttons + locks the modal. */
  running?: boolean;

  /** Optional error message to surface above the consequences. */
  error?: string | null;

  /**
   * Called when the user clicks Confirm. The reason field's current
   * value is passed in (empty string if hidden / un-required and
   * blank). Caller is responsible for executing the action.
   */
  onConfirm: (reason: string) => void;

  /** Called when the user closes the modal without confirming. */
  onCancel: () => void;

  /**
   * Called on every keystroke in the reason field (and with the initial
   * reason on open), so `children` can react to what is being typed, for
   * example a preview of an email that quotes the reason.
   */
  onReasonChange?: (reason: string) => void;
}

export function DestructiveActionConfirm({
  open,
  actionName,
  consequences,
  reversibilityNote,
  showReasonField = true,
  reasonRequired = false,
  initialReason = "",
  reasonPlaceholder = "Reason for the audit log…",
  confirmLabel = "Confirm",
  confirmTone = "danger",
  warningBanner,
  confirmPhrase,
  confirmPhraseExplanation,
  cancelLabel = "Cancel",
  running = false,
  error = null,
  children,
  onConfirm,
  onCancel,
  onReasonChange,
}: DestructiveActionConfirmProps) {
  const [reason, setReason] = useState(initialReason);
  const [phrasePassed, setPhrasePassed] = useState(false);
  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const phraseOk = !confirmPhrase || phrasePassed;

  // Stale-state audit fix (2026-05-29) — this dialog persists at its
  // call site (host renders <DestructiveActionConfirm open={...}/> and
  // toggles `open`), so without resetting on open it shows the PREVIOUS
  // target's typed reason when reopened for a different target. Reset
  // the reason to the (target-specific) initialReason whenever the
  // dialog opens.
  useEffect(() => {
    if (open) {
      setReason(initialReason);
      setPhrasePassed(false);
      onReasonChange?.(initialReason);
    }
    // onReasonChange is deliberately not a dependency: callers pass a fresh
    // closure, and re-firing on every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialReason]);

  return (
    <Modal
      open={open}
      title={actionName}
      onClose={running ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={running}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmTone === "danger" ? "danger" : "primary"}
            onClick={() => onConfirm(reason)}
            disabled={running || !reasonOk || !phraseOk}
          >
            {running ? "Running…" : confirmLabel}
          </Button>
        </>
      }
    >
      <Stack gap={10}>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        {warningBanner && (
          <div
            role="alert"
            style={{
              background: "rgba(192, 57, 43, 0.06)",
              border: "1px solid rgba(192, 57, 43, 0.3)",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 12,
              color: "var(--danger, #c0392b)",
            }}
          >
            <strong>{warningBanner}</strong>
          </div>
        )}

        {consequences.length > 0 && (
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600 }}>
              What this does, in plain English:
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {consequences.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {children}

        {reversibilityNote && (
          <p
            className="muted"
            style={{ margin: 0, fontSize: 12, fontStyle: "italic" }}
          >
            {reversibilityNote}
          </p>
        )}

        {confirmPhrase && (
          <TypeToConfirmGate
            phrase={confirmPhrase}
            explanation={confirmPhraseExplanation ?? ""}
            onPassChange={setPhrasePassed}
            disabled={running}
          />
        )}

        {showReasonField && (
          <div>
            <label
              htmlFor="dac-reason"
              style={{ display: "block", fontSize: 13, marginBottom: 4 }}
            >
              {reasonRequired ? "Reason (required, audit-logged)" : "Reason (optional, audit-logged)"}
            </label>
            <textarea
              id="dac-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                onReasonChange?.(e.target.value);
              }}
              disabled={running}
              rows={2}
              placeholder={reasonPlaceholder}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--mid-gray)",
                borderRadius: "var(--radius-compact)",
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
          </div>
        )}
      </Stack>
    </Modal>
  );
}
